import React, { useState } from "react";
import axios from "axios";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import { MdOutlinePlayCircleOutline } from "react-icons/md";
import DisplayAmount from "../../shared/DisplayAmount";

const BatchProcessConfirmModal: React.FC<{
  batchToProcessConfirm: any;
  isOpen: boolean;
  onClose(): void;
  triggerReload(): void;
  onProcessingStarted?(batchId: string, optimisticStatus?: string): void;
  setSwalProps: any;
}> = ({
  isOpen,
  onClose,
  triggerReload,
  onProcessingStarted,
  batchToProcessConfirm,
  setSwalProps,
}) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const closeModal = async () => {
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setIsProcessing(true);

      const postData = {
        batch_id: batchToProcessConfirm.id,
        is_manual: batchToProcessConfirm.is_manual,
        username: localStorage.getItem("username"),
        userRole: userRole,
        subaccount_id: batchToProcessConfirm.subaccount.id,
      };

      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/batch/action/batch-process-provider`,
          postData
        )
        .then(function (response) {
          if (response && response.data?.status === "ok") {
            onClose();
            setSwalProps({
              show: true,
              icon: "success",
              title: "Success",
              html: "The batch has been processed",
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsProcessing(false);
                if (onProcessingStarted) {
                  onProcessingStarted(batchToProcessConfirm.id);
                } else {
                  triggerReload();
                }
              },
            });
          } else {
            setIsProcessing(false);
          }
        })
        .catch(function (error) {
          setIsProcessing(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: error.response.data.message,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsProcessing(false);
            },
          });
        });
    } catch (error: any) {
      setIsProcessing(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsProcessing(false);
        },
      });
    }
  };

  return (
    <>
      {batchToProcessConfirm && (
        <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
          <ModalOverlay className="bg-[#000] !opacity-30" />
          <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-2xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
            <ModalBody className="p-6 md:p-7">
              <div className="mb-4 flex items-start gap-3">
                <div className="rounded-xl bg-amber-50 p-2 text-amber-600 ring-1 ring-amber-100 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-900/30">
                  <MdOutlinePlayCircleOutline className="h-5 w-5 text-base" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Process Batch
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                You're about to <span className="font-semibold">process</span>{" "}
                the current batch. This action cannot be undone.
              </div>

              <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                <div className="flex flex-col gap-3 text-sm">
                  {userRole === "superadmin" &&
                    batchToProcessConfirm.provider_name &&
                    batchToProcessConfirm.provider_account_name &&
                    batchToProcessConfirm.provider_method_name && (
                      <>
                        <div className="flex">
                          <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                            Provider
                          </div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {batchToProcessConfirm.provider_name} (
                            {batchToProcessConfirm.provider_account_name})
                          </div>
                        </div>
                        <div className="flex">
                          <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                            Provider Method
                          </div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            {batchToProcessConfirm.provider_method_name}
                          </div>
                        </div>
                      </>
                    )}

                  {userRole !== "superadmin" && (
                    <div className="flex">
                      <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                        Reference
                      </div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {batchToProcessConfirm.batch_reference}
                      </div>
                    </div>
                  )}

                  <div className="flex">
                    <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                      Record(s)
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {batchToProcessConfirm.record}
                    </div>
                  </div>

                  <div className="flex">
                    <div className="w-40 shrink-0 text-gray-700 dark:text-gray-400">
                      Amount
                    </div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {batchToProcessConfirm.crypto ? (
                        batchToProcessConfirm.amount
                      ) : (
                        <DisplayAmount
                          amount={Number(batchToProcessConfirm.amount)}
                          currency={batchToProcessConfirm.asset}
                        />
                      )}{" "}
                      {batchToProcessConfirm.asset}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => closeModal()}
                  isDisabled={isProcessing}
                  className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                >
                  Not now
                </Button>

                <Button
                  onClick={() => handleSubmit()}
                  isDisabled={isProcessing}
                  isLoading={isProcessing}
                  loadingText="Processing..."
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                >
                  Process batch
                </Button>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default BatchProcessConfirmModal;
